import { type FormEvent, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { RevealStoredValue } from '@/components/ui/RevealStoredValue';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import {
  type ConfigSchemaField,
  type DistributorSettings,
  revealOrgDistributorCredentials,
  useDistributorSettings,
  useReorderDistributorSettings,
  useTestDistributorCredentials,
  useUpdateDistributorSettings,
} from '@/lib/distributors';

interface Props {
  orgSlug: string;
}

/**
 * Per-org distributor integrations panel. Lists every distributor
 * PartForge knows about and lets the org admin enable each one,
 * choose between site-wide credentials (when available) or supply
 * their own, and reorder them by drag.
 *
 * Drag handles use the native HTML5 drag-and-drop API — no extra
 * dependency, the list is short (≤ ten rows). Drop fan-outs the
 * new priority order to the per-row PUT endpoint in parallel; the
 * server already supports it, the mutation just batches calls.
 */
export function DistributorSettings({ orgSlug }: Props) {
  const settings = useDistributorSettings(orgSlug);
  const reorder = useReorderDistributorSettings(orgSlug);

  // Local copy of the ordered list so drag movements update the
  // rendering immediately, before the server confirms. Reseeded on
  // every fresh fetch so a remote edit from another tab doesn't
  // get stomped by stale local state.
  const [ordered, setOrdered] = useState<DistributorSettings[]>([]);
  useEffect(() => {
    if (settings.data) setOrdered(settings.data);
  }, [settings.data]);

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const onDragStart = (idx: number) => () => {
    setDragIndex(idx);
  };
  const onDragOver = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    if (idx !== hoverIndex) setHoverIndex(idx);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setHoverIndex(null);
  };
  const onDrop = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) {
      onDragEnd();
      return;
    }
    const next = [...ordered];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    setOrdered(next);
    onDragEnd();
    reorder.mutate(next);
  };

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">Distributor integrations</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Enable distributors so you can paste a product URL or part
        number into PartForge and have us auto-fill the part. Drag rows
        to set the priority — lower wins for shopping-list grouping
        and find-alternates ordering.
      </p>

      {settings.isPending && (
        <p className="mt-4 text-sm text-stone-500 dark:text-stone-400">Loading…</p>
      )}

      {settings.isError && (
        <p className="mt-4 text-sm text-red-600">
          {(settings.error as Error).message}
        </p>
      )}

      {reorder.isError && (
        <p className="mt-2 text-sm text-red-600">
          Reorder failed: {(reorder.error as Error).message}
        </p>
      )}

      {ordered.length > 0 && (
        <ul className="mt-4 divide-y divide-stone-100 dark:divide-stone-800 -mx-5">
          {ordered.map((row, idx) => (
            <DistributorRow
              key={row.distributor_slug}
              orgSlug={orgSlug}
              row={row}
              draggable={row.adapter_available}
              dragging={dragIndex === idx}
              dropTarget={
                hoverIndex === idx && dragIndex !== null && dragIndex !== idx
              }
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver(idx)}
              onDragEnd={onDragEnd}
              onDrop={onDrop(idx)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function DistributorRow({
  orgSlug,
  row,
  draggable,
  dragging,
  dropTarget,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: {
  orgSlug: string;
  row: DistributorSettings;
  draggable: boolean;
  dragging: boolean;
  dropTarget: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent<HTMLLIElement>) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent<HTMLLIElement>) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Quick visual signal for the user. Ordering: most actionable first.
  const status = useMemo(() => statusFor(row), [row]);

  if (!row.adapter_available) {
    // Render but disable. The user might still want to track that the
    // distributor exists in PartForge (priority for shopping lists) but
    // we can't actually look anything up against it yet.
    return (
      <li className="flex items-start gap-3 px-5 py-3 opacity-60">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.distributor_name}</span>
            <span className="rounded-full bg-stone-100 dark:bg-stone-800 px-2 py-0.5 text-xs text-stone-500 dark:text-stone-400">
              not yet supported
            </span>
          </div>
          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
            PartForge knows about {row.distributor_name} but doesn't have a
            lookup adapter for it yet. Coming in a later release.
          </p>
        </div>
      </li>
    );
  }

  return (
    <li
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      className={cn(
        'px-5 py-3 transition-colors',
        dragging && 'opacity-40',
        // Visual cue for the drop slot — a thin top border so the user
        // sees where the dragged row will land.
        dropTarget && 'border-t-2 border-forge-500',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          title="Drag to reorder"
          className="mt-1 cursor-grab select-none text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 active:cursor-grabbing"
        >
          ⋮⋮
        </span>
        <div className="flex-1">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="flex w-full items-center gap-2 text-left"
          >
            <span aria-hidden className="text-stone-400 dark:text-stone-500">
              {expanded ? '▾' : '▸'}
            </span>
            <span className="font-medium">{row.distributor_name}</span>
            <StatusPill status={status} />
            {row.last_error && (
              <span
                className="ml-2 truncate text-xs text-red-600"
                title={row.last_error}
              >
                ⚠ {row.last_error}
              </span>
            )}
          </button>

          {!expanded && row.enabled && (
            <p className="mt-1 ml-5 text-xs text-stone-500 dark:text-stone-400">
              Priority {row.priority} ·{' '}
              {row.use_system_credentials && row.system_credentials_enabled
                ? 'using site-wide credentials'
                : row.has_org_credentials
                  ? 'using your own credentials'
                  : 'no credentials configured yet'}
              {row.daily_request_count > 0 && (
                <> · {row.daily_request_count} requests today</>
              )}
            </p>
          )}
        </div>
      </div>

      {expanded && <DistributorEditor orgSlug={orgSlug} row={row} />}
    </li>
  );
}

function StatusPill({ status }: { status: ReturnType<typeof statusFor> }) {
  const styles = {
    enabled: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    needs_creds:
      'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    revoked: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    disabled:
      'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400',
  } as const;
  const labels = {
    enabled: 'Enabled',
    needs_creds: 'Needs credentials',
    revoked: 'Shared access revoked',
    disabled: 'Disabled',
  } as const;
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        styles[status],
      )}
    >
      {labels[status]}
    </span>
  );
}

function statusFor(row: DistributorSettings) {
  if (!row.enabled) return 'disabled';
  if (!row.system_credentials_allowed && !row.has_org_credentials) {
    return 'revoked';
  }
  if (row.use_system_credentials) {
    if (row.system_credentials_enabled) return 'enabled';
    if (row.has_org_credentials) return 'enabled'; // fallback configured
    return 'needs_creds';
  }
  return row.has_org_credentials ? 'enabled' : 'needs_creds';
}

function DistributorEditor({
  orgSlug,
  row,
}: {
  orgSlug: string;
  row: DistributorSettings;
}) {
  const update = useUpdateDistributorSettings(orgSlug);

  const [enabled, setEnabled] = useState(row.enabled);
  // Priority is owned by the drag-sortable list above — never edited
  // here. We forward `row.priority` unchanged on every save so the
  // existing PUT body shape (which requires priority) stays valid.
  const priority = row.priority;
  const [useSystem, setUseSystem] = useState(row.use_system_credentials);
  // Form state seeds from the saved non-secret values so the user can
  // see and edit what they entered. Secret fields stay empty in the
  // draft (the server keeps them on the row) — submitting with a blank
  // secret means "keep what's stored" rather than "clear it".
  const [credentialsDraft, setCredentialsDraft] = useState<
    Record<string, unknown>
  >(row.credentials ?? {});
  const [credentialsTouched, setCredentialsTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Re-sync local form state when the upstream row changes (e.g. the
  // user navigated away and back, or saved + the cache refreshed).
  useEffect(() => {
    setEnabled(row.enabled);
    setUseSystem(row.use_system_credentials);
    setCredentialsDraft(row.credentials ?? {});
    setCredentialsTouched(false);
  }, [row.distributor_slug, row.credentials]);

  const secretFieldsSet = new Set(row.secret_fields_set ?? []);
  const test = useTestDistributorCredentials(orgSlug);

  const fields = row.config_schema?.fields ?? [];
  const showCredsForm = !useSystem || !row.system_credentials_enabled;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setShowSuccess(false);
    update.mutate(
      {
        distributor_slug: row.distributor_slug,
        enabled,
        priority,
        use_system_credentials: useSystem,
        credentials: credentialsTouched ? credentialsDraft : null,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setCredentialsTouched(false);
          setCredentialsDraft({});
        },
      },
    );
  };

  const onClearCreds = () => {
    if (!confirm(`Clear your stored ${row.distributor_name} credentials?`)) {
      return;
    }
    update.mutate({
      distributor_slug: row.distributor_slug,
      enabled,
      priority,
      use_system_credentials: useSystem,
      clear_credentials: true,
    });
  };

  const adminRevokedShared = !row.system_credentials_allowed;

  return (
    <form
      onSubmit={onSubmit}
      className="mt-3 ml-5 space-y-3 rounded-md bg-stone-50 dark:bg-stone-950/40 p-3"
    >
      {row.distributor_website && (
        <p className="text-xs text-stone-500 dark:text-stone-400">
          <a
            href={row.distributor_website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-forge-700 dark:text-forge-400 hover:underline"
          >
            {row.distributor_website}
          </a>
        </p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Enable {row.distributor_name} lookups
      </label>

      {!adminRevokedShared && (
        <fieldset
          disabled={!enabled}
          className="space-y-1 rounded border border-stone-200 dark:border-stone-700 p-2"
        >
          <legend className="px-1 text-xs font-medium text-stone-600 dark:text-stone-400">
            Credentials source
          </legend>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              checked={useSystem}
              onChange={() => setUseSystem(true)}
              className="mt-1"
            />
            <span>
              <span className="font-medium">Use site-wide credentials</span>
              <span
                className={cn(
                  'ml-2 rounded px-1.5 py-0.5 text-xs',
                  row.system_credentials_enabled
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300',
                )}
              >
                {row.system_credentials_enabled ? 'available' : 'not configured'}
              </span>
              <p className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
                Set up by your PartForge administrator. You don't see the
                values, only whether they're working.
              </p>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              checked={!useSystem}
              onChange={() => setUseSystem(false)}
              className="mt-1"
            />
            <span>
              <span className="font-medium">Use my own credentials</span>
              <p className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
                Stored encrypted, only used for this organization.
              </p>
            </span>
          </label>
        </fieldset>
      )}

      {adminRevokedShared && (
        <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-2 text-xs text-amber-900 dark:text-amber-200">
          Your administrator has disabled site-wide credentials for this
          organization. You'll need to supply your own credentials below.
        </div>
      )}

      {showCredsForm && fields.length > 0 && (
        <div className="space-y-2 rounded border border-stone-200 dark:border-stone-700 p-2">
          <p className="text-xs font-medium text-stone-600 dark:text-stone-400">
            {row.distributor_name} credentials
            {row.has_org_credentials && (
              <span className="ml-2 text-xs text-stone-500 dark:text-stone-500">
                — leave secret fields blank to keep their stored values
              </span>
            )}
          </p>
          {fields.map((f) => (
            <CredentialField
              key={f.key}
              field={f}
              value={credentialsDraft[f.key]}
              hasStoredSecret={f.secret === true && secretFieldsSet.has(f.key)}
              onChange={(value) => {
                setCredentialsDraft((prev) => ({ ...prev, [f.key]: value }));
                setCredentialsTouched(true);
              }}
              revealFetcher={
                f.secret === true && secretFieldsSet.has(f.key)
                  ? async () => {
                      const creds = await revealOrgDistributorCredentials(
                        orgSlug,
                        row.distributor_slug,
                      );
                      return creds[f.key] ?? null;
                    }
                  : undefined
              }
            />
          ))}
          {row.has_org_credentials && (
            <button
              type="button"
              onClick={onClearCreds}
              className="text-xs text-red-600 hover:underline"
            >
              Clear stored credentials
            </button>
          )}
        </div>
      )}

      {update.isError && (
        <p className="text-sm text-red-600">
          {(update.error as Error).message}
        </p>
      )}
      {showSuccess && (
        <p className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</p>
      )}

      {(row.has_org_credentials || row.system_credentials_enabled)
        && row.adapter_available && (
        <div className="flex flex-col gap-1.5">
          {test.data && (
            <p
              className={cn(
                'rounded-md border px-2 py-1.5 text-xs',
                test.data.ok
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300',
              )}
            >
              {test.data.ok ? '✓ ' : '✗ '}
              {test.data.detail}
            </p>
          )}
          {test.isError && (
            <p className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800">
              ✗ Test failed: {(test.error as Error).message}
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {(row.has_org_credentials || row.system_credentials_enabled)
          && row.adapter_available && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => test.mutate(row.distributor_slug)}
            disabled={test.isPending}
            title={
              `Hit a cheap ${row.distributor_name} endpoint to confirm `
              + 'the configured credentials work. For DigiKey this just '
              + 'mints an OAuth token (no product-API quota); for LCSC '
              + 'it fetches one well-known PN.'
            }
          >
            {test.isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Testing…
              </>
            ) : (
              'Test connection'
            )}
          </Button>
        )}
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </form>
  );
}

function CredentialField({
  field,
  value,
  hasStoredSecret,
  onChange,
  revealFetcher,
}: {
  field: ConfigSchemaField;
  value: unknown;
  /** True when this is a secret field AND the server has a stored
   * value for it. Renders a "saved — leave blank to keep" placeholder so the
   * user knows they can leave the field blank to preserve the secret. */
  hasStoredSecret: boolean;
  onChange: (value: unknown) => void;
  /** When provided, renders a "Reveal stored value" affordance below
   * the input that fetches the decrypted value on click. Only set
   * for secret-flagged fields with a stored value. */
  revealFetcher?: () => Promise<string | null>;
}) {
  if (field.type === 'bool') {
    const checked = typeof value === 'boolean' ? value : Boolean(field.default);
    return (
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span>{field.label}</span>
          {field.warning && (
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
              {field.warning}
            </p>
          )}
        </span>
      </label>
    );
  }
  const placeholder = field.secret && hasStoredSecret
    ? 'saved — leave blank to keep'
    : undefined;
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs text-stone-600 dark:text-stone-400">
        {field.label}
        {field.secret && (
          <span className="ml-2 text-stone-400 dark:text-stone-500">(secret)</span>
        )}
      </span>
      {field.secret ? (
        <PasswordInput
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <Input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {revealFetcher && (
        <RevealStoredValue
          fetcher={revealFetcher}
          onReveal={(v) => onChange(v)}
          onHide={() => onChange('')}
          label="stored value"
        />
      )}
      {field.warning && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
          {field.warning}
        </p>
      )}
    </label>
  );
}
